import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, FileWarning, CalendarClock, TrendingDown, Loader2 } from "lucide-react";
import api, { money, d } from "@/api";
import { Badge, MarginTag, MODE_LABELS } from "@/components/Badge";

const Metric = ({ label, value, icon: Icon, tone, testId }) => (
  <div className={`brut p-4 sm:p-6 ${tone}`} data-testid={testId}>
    <div className="flex items-start justify-between">
      <p className="overline">{label}</p>
      <Icon className="h-4 w-4 text-zinc-400" />
    </div>
    <p className="tnum mt-3 font-display text-3xl font-black tracking-tighter">{value}</p>
  </div>
);

const Panel = ({ title, children, testId, count }) => (
  <div className="brut" data-testid={testId}>
    <div className="flex items-center justify-between border-b border-zinc-950 px-4 py-3">
      <p className="overline">{title}</p>
      <span className="tnum font-mono text-xs text-zinc-500">{count}</span>
    </div>
    <div className="max-h-80 overflow-auto">{children}</div>
  </div>
);

const Empty = ({ text }) => <p className="px-4 py-8 text-sm text-zinc-500">{text}</p>;

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [fx, setFx] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => setData({
      metrics: { active_operations: 0, delayed: 0, missing_documents: 0, low_margin: 0, total_profit_try: 0 },
      delayed: [], upcoming: [], missing_documents: [], low_margin: [], recent: [],
    }));
    api.get("/fx/rates").then((r) => setFx(r.data)).catch(() => {});
  }, []);

  if (!data)
    return (
      <div className="flex h-64 items-center justify-center" data-testid="dashboard-loading">
        <Loader2 className="h-6 w-6 animate-spin text-klein" />
      </div>
    );

  const m = data.metrics;
  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="overline">Operasyon Merkezi</p>
          <h1 className="font-display text-4xl font-black tracking-tighter sm:text-5xl">Panel</h1>
        </div>
        {fx && (
          <div className="brut px-4 py-2" data-testid="fx-widget">
            <p className="overline">Kur ({fx.source === "frankfurter" ? "canlı" : "manuel"})</p>
            <p className="tnum font-mono text-sm">
              USD {fx.rates.USD} · EUR {fx.rates.EUR} · GBP {fx.rates.GBP}
            </p>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Metric label="Aktif Operasyon" value={m.active_operations} icon={CalendarClock} testId="metric-active" />
        <Metric label="Geciken İş" value={m.delayed} icon={AlertTriangle} tone={m.delayed ? "border-red-300 bg-red-50" : ""} testId="metric-delayed" />
        <Metric label="Eksik Evrak" value={m.missing_documents} icon={FileWarning} tone={m.missing_documents ? "border-yellow-300 bg-yellow-50" : ""} testId="metric-missing-docs" />
        <Metric label="Düşük Marjlı Dosya" value={m.low_margin} icon={TrendingDown} testId="metric-low-margin" />
      </div>

      <div className="brut flex flex-wrap items-center justify-between gap-3 px-4 py-3" data-testid="total-profit-card">
        <p className="overline">Toplam Beklenen Kâr</p>
        <p className="tnum font-mono text-xl font-semibold">{money(m.total_profit_try)}</p>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Panel title="Geciken İşler" testId="panel-delayed" count={data.delayed.length}>
          {data.delayed.length === 0 ? (
            <Empty text="Geciken operasyon yok." />
          ) : (
            data.delayed.map((r) => (
              <Link key={r.id} to={`/operasyonlar/${r.id}`} className="block border-b border-zinc-100 px-4 py-3 hover:bg-red-50">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-sm font-semibold">{r.no}</span>
                  <span className="tnum text-xs text-red-700">{r.total_delay_hours} saat gecikme</span>
                </div>
                <p className="text-xs text-zinc-500">{r.customer_name} · {r.route}</p>
              </Link>
            ))
          )}
        </Panel>

        <Panel title="Eksik Evraklar" testId="panel-missing-docs" count={data.missing_documents.length}>
          {data.missing_documents.length === 0 ? (
            <Empty text="Eksik evrak yok." />
          ) : (
            data.missing_documents.map((r) => (
              <Link key={r.id} to={`/operasyonlar/${r.operation_id}`} className="block border-b border-zinc-100 px-4 py-3 hover:bg-yellow-50">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold">{r.name}</span>
                  <span className={`text-xs ${r.is_overdue ? "font-semibold text-red-600" : "text-zinc-500"}`}>
                    {r.due_date ? d(r.due_date) : "-"}
                  </span>
                </div>
                <p className="font-mono text-xs text-zinc-500">{r.operation_no}</p>
              </Link>
            ))
          )}
        </Panel>

        <Panel title="Yaklaşan Teslimatlar" testId="panel-upcoming" count={data.upcoming.length}>
          {data.upcoming.length === 0 ? (
            <Empty text="7 gün içinde teslimat yok." />
          ) : (
            data.upcoming.map((r) => (
              <Link key={r.id} to={`/operasyonlar/${r.id}`} className="block border-b border-zinc-100 px-4 py-3 hover:bg-zinc-50">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-sm font-semibold">{r.no}</span>
                  <span className="text-xs text-zinc-600">{d(r.eta)}</span>
                </div>
                <p className="text-xs text-zinc-500">{MODE_LABELS[r.mode]} · {r.route}</p>
              </Link>
            ))
          )}
        </Panel>

        <Panel title="Düşük Marjlı Dosyalar" testId="panel-low-margin" count={data.low_margin.length}>
          {data.low_margin.length === 0 ? (
            <Empty text="Düşük marjlı dosya yok." />
          ) : (
            data.low_margin.map((r) => (
              <Link key={r.id} to={`/operasyonlar/${r.id}`} className="block border-b border-zinc-100 px-4 py-3 hover:bg-zinc-50">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-sm font-semibold">{r.no}</span>
                  <MarginTag pct={r.margin_pct} />
                </div>
                <p className="text-xs text-zinc-500">{r.customer_name} · {money(r.profit_try)}</p>
              </Link>
            ))
          )}
        </Panel>
      </div>

      <div className="brut" data-testid="recent-operations">
        <div className="border-b border-zinc-950 px-4 py-3">
          <p className="overline">Son Operasyonlar</p>
        </div>
        <div className="overflow-x-auto">
          <table className="dense w-full">
            <thead>
              <tr>
                <th>Poz No</th><th>Müşteri</th><th>Rota</th><th>Mod</th><th>Durum</th>
                <th className="!text-right">Marj</th>
              </tr>
            </thead>
            <tbody>
              {data.recent.map((r, i) => (
                <tr key={r.id} className={i % 2 ? "bg-zinc-50" : ""}>
                  <td>
                    <Link to={`/operasyonlar/${r.id}`} className="font-mono font-semibold text-klein hover:underline">
                      {r.no}
                    </Link>
                  </td>
                  <td>{r.customer_name}</td>
                  <td className="text-xs">{r.origin} → {r.destination}</td>
                  <td>{MODE_LABELS[r.mode]}</td>
                  <td><Badge value={r.status} /></td>
                  <td className="text-right"><MarginTag pct={r.margin_pct} /></td>
                </tr>
              ))}
              {data.recent.length === 0 && (
                <tr><td colSpan={6} className="py-8 text-center text-zinc-500">Henüz operasyon kaydı yok.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
